<script setup lang="ts">
    import { ref, computed, watch, onMounted } from 'vue';
    import { api } from '@cwd/lib/main';
    import { setupTags } from '@shared/components/Tags/Tags';
    import type { Category } from '../../Contact/ContactTypes';
    import type { AdditionalField, AddedFilter, PresetFilter } from '../ListsTypes.d';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import TriStateSwitch from '@shared/components/Form/TriStateSwitch.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';

    const props = defineProps<{
        availableFields: AdditionalField[];
        scalarOnly?: boolean;
        showTags?: boolean;
        /** Target endpoint parses OData v4 (Microsoft.AspNetCore.OData) — guid literals must be bare, no prefix. */
        odataV4?: boolean;
    }>();

    function guidLiteral(id: string) {
        return props.odataV4 ? id : `guid'${id}'`;
    }

    const emit = defineEmits<{
        (e: 'change', odataFilter: string): void;
        (e: 'update:filterState', state: Partial<SerializedContactFilterState>): void;
    }>();

    // --- Tags & categories ---

    const tags = setupTags();
    const categories = ref<Category[]>([]);

    onMounted(async () => {
        const initialFilter = buildOdataFilter();
        if (initialFilter) emit('change', initialFilter);
        emit('update:filterState', serializeFilterState());
        try {
            categories.value = await api.get('/Categories');
        } catch {
            /* ignore */
        }
    });

    // --- Filter operators ---

    interface FilterOperator {
        operator: string;
        displayName: string;
        types: string[];
    }

    const allOperators: FilterOperator[] = [
        { operator: 'eq', displayName: $Resources.FilterOperatorsEqual, types: ['string', 'datetime', 'number', 'boolean'] },
        { operator: 'ne', displayName: $Resources.FilterOperatorsNotEqual, types: ['string', 'datetime', 'number', 'boolean'] },
        { operator: 'contains', displayName: $Resources.FilterOperatorsContains, types: ['string'] },
        { operator: 'notContains', displayName: $Resources.FilterOperatorsContainsNot, types: ['string'] },
        { operator: 'startsWith', displayName: $Resources.FilterOperatorsStartsWith, types: ['string'] },
        { operator: 'notStartsWith', displayName: $Resources.FilterOperatorsNotStartsWith, types: ['string'] },
        { operator: 'endsWith', displayName: $Resources.FilterOperatorsEndsWith, types: ['string'] },
        { operator: 'gt', displayName: $Resources.FilterOperatorsGreaterThan, types: ['datetime', 'number'] },
        { operator: 'lt', displayName: $Resources.FilterOperatorsLessThan, types: ['datetime', 'number'] },
        { operator: 'empty', displayName: $Resources.FilterOperatorsEmptyLabel, types: ['string', 'datetime', 'number', 'boolean'] },
        { operator: 'notEmpty', displayName: $Resources.FilterOperatorsNotEmptyLabel, types: ['string', 'datetime', 'number', 'boolean'] },
    ];

    const booleanOptions = [
        { value: 'true', label: $Resources.FilterOperatorsTrueLabel },
        { value: 'false', label: $Resources.FilterOperatorsFalseLabel },
        { value: '__null__', label: $Resources.FilterOperatorsValueNotSetLabel },
    ];

    // --- Field/Operator/Value builder state ---

    const selectedFieldName = ref('');
    const selectedOperatorKey = ref('');
    const filterText = ref('');

    const selectedField = computed(() => props.availableFields.find((f) => f.name === selectedFieldName.value) ?? null);
    const selectedOperator = computed(() => matchingOperators.value.find((op) => op.operator === selectedOperatorKey.value) ?? null);

    const fieldOptions = computed(() => props.availableFields.map((f) => ({ value: f.name, label: f.displayName })));
    const operatorOptions = computed(() => matchingOperators.value.map((op) => ({ value: op.operator, label: op.displayName })));

    watch(selectedFieldName, () => {
        selectedOperatorKey.value = '';
        filterText.value = '';
    });

    const addedFilters = ref<AddedFilter[]>([]);
    const andOrOperator = ref<'and' | 'or'>('and');

    const matchingOperators = computed(() => {
        if (!selectedField.value) return [];
        const type = selectedField.value.type;
        return allOperators.filter((op) => {
            if (!op.types.includes(type)) return false;
            if (type === 'boolean') return op.operator === 'eq' || op.operator === 'ne' || op.operator === 'empty' || op.operator === 'notEmpty';
            return true;
        });
    });

    const enableFilterText = computed(() => {
        return selectedOperator.value && selectedOperator.value.operator !== 'empty' && selectedOperator.value.operator !== 'notEmpty';
    });

    const canAddFilter = computed(() => {
        if (!selectedField.value || !selectedOperator.value) return false;
        if (selectedField.value.type === 'boolean') return !!filterText.value;
        if (selectedOperator.value.operator === 'empty' || selectedOperator.value.operator === 'notEmpty') return true;
        return true;
    });

    function addFilter() {
        if (!selectedField.value || !selectedOperator.value) return;

        let finalOp = selectedOperator.value;
        let finalText = filterText.value;

        if (selectedField.value.type === 'boolean' && filterText.value === '__null__') {
            const targetName = finalOp.operator === 'eq' ? 'empty' : 'notEmpty';
            const mapped = allOperators.find((o) => o.operator === targetName);
            if (mapped) {
                finalOp = mapped;
                finalText = '';
            }
        }

        addedFilters.value.push({
            fieldName: selectedField.value.name,
            fieldDisplayName: selectedField.value.displayName,
            fieldType: selectedField.value.type,
            operator: finalOp.operator,
            operatorName: finalOp.displayName,
            text: finalText,
        });

        selectedFieldName.value = '';
        selectedOperatorKey.value = '';
        filterText.value = '';
    }

    function removeFilter(index: number) {
        addedFilters.value.splice(index, 1);
    }

    function clearStatusIsBlocked() {
        statusIsBlocked.value = null;
    }
    function clearStatusIsSmsBlocked() {
        statusIsSmsBlocked.value = null;
    }
    function clearStatusHasBounced() {
        statusHasBounced.value = null;
    }
    function clearStatusHasSmsBounced() {
        statusHasSmsBounced.value = null;
    }
    function clearMissingTags() {
        missingTags.value = [];
    }
    function clearActiveCategories() {
        activeCategories.value = [];
    }
    function clearBlockedCategories() {
        blockedCategories.value = [];
    }
    function clearAllFilters() {
        addedFilters.value = [];
        statusIsBlocked.value = null;
        statusIsSmsBlocked.value = null;
        statusHasBounced.value = null;
        statusHasSmsBounced.value = null;
        missingTags.value = [];
        activeCategories.value = [];
        blockedCategories.value = [];
        tags.clear();
    }

    export interface SerializedContactFilterState {
        filters: PresetFilter[];
        andOrOperator: 'and' | 'or';
        statusIsBlocked: boolean | null;
        statusIsSmsBlocked: boolean | null;
        statusHasBounced: boolean | null;
        statusHasSmsBounced: boolean | null;
        selectedTagIds: string[];
        missingTagIds: string[];
        activeCategoryIds: string[];
        blockedCategoryIds: string[];
    }

    // Only non-default fields are included, keeping the serialized (URL-bound) state compact.
    function serializeFilterState(): Partial<SerializedContactFilterState> {
        const state: Partial<SerializedContactFilterState> = {};
        if (addedFilters.value.length) state.filters = addedFilters.value.map((f) => ({ fieldName: f.fieldName, operator: f.operator, text: f.text }));
        if (andOrOperator.value !== 'and') state.andOrOperator = andOrOperator.value;
        if (statusIsBlocked.value !== null) state.statusIsBlocked = statusIsBlocked.value;
        if (statusIsSmsBlocked.value !== null) state.statusIsSmsBlocked = statusIsSmsBlocked.value;
        if (statusHasBounced.value !== null) state.statusHasBounced = statusHasBounced.value;
        if (statusHasSmsBounced.value !== null) state.statusHasSmsBounced = statusHasSmsBounced.value;
        if (tags.state.selectedIds.length) state.selectedTagIds = tags.state.selectedIds;
        if (missingTags.value.length) state.missingTagIds = missingTags.value;
        if (activeCategories.value.length) state.activeCategoryIds = activeCategories.value;
        if (blockedCategories.value.length) state.blockedCategoryIds = blockedCategories.value;
        return state;
    }

    function restoreFilterState(state: Partial<SerializedContactFilterState>) {
        clearAllFilters();
        setPresetFilters(state.filters ?? []);
        andOrOperator.value = state.andOrOperator ?? 'and';
        statusIsBlocked.value = state.statusIsBlocked ?? null;
        statusIsSmsBlocked.value = state.statusIsSmsBlocked ?? null;
        statusHasBounced.value = state.statusHasBounced ?? null;
        statusHasSmsBounced.value = state.statusHasSmsBounced ?? null;
        tags.state.selectedIds = state.selectedTagIds ?? [];
        missingTags.value = state.missingTagIds ?? [];
        activeCategories.value = state.activeCategoryIds ?? [];
        blockedCategories.value = state.blockedCategoryIds ?? [];
    }

    function setPresetFilters(presets: PresetFilter[]) {
        clearAllFilters();
        for (const p of presets) {
            const field = props.availableFields.find((f) => f.name === p.fieldName);
            if (!field) continue;
            const op = allOperators.find((o) => o.operator === p.operator);
            if (!op) continue;
            addedFilters.value.push({
                fieldName: field.name,
                fieldDisplayName: field.displayName,
                fieldType: field.type,
                operator: op.operator,
                operatorName: op.displayName,
                text: p.text,
            });
        }
    }

    function getFilterValueDisplay(f: AddedFilter): string {
        if (f.operator === 'empty' || f.operator === 'notEmpty') return '';
        if (f.fieldType === 'boolean') {
            if (f.text === 'true') return $Resources.FilterOperatorsTrueLabel;
            if (f.text === 'false') return $Resources.FilterOperatorsFalseLabel;
            return $Resources.FilterOperatorsValueNotSetLabel;
        }
        // datetime-local's 'T' separator is only needed in the OData literal (buildOdataFilter uses
        // f.text directly, unchanged) — cosmetic-only here, for the human-readable filter summary.
        if (f.fieldType === 'datetime') return f.text.replace('T', ' ');
        return f.text;
    }

    // --- Status toggles (tri-state: null=off, true, false) ---

    const statusIsBlocked = ref<boolean | null>(null);
    const statusIsSmsBlocked = ref<boolean | null>(null);
    const statusHasBounced = ref<boolean | null>(null);
    const statusHasSmsBounced = ref<boolean | null>(null);

    // --- Tag filters ---

    const missingTags = ref<string[]>([]);

    // --- Category filters ---

    const activeCategories = ref<string[]>([]);
    const blockedCategories = ref<string[]>([]);

    // --- Filter count ---

    const filterCount = computed(() => {
        let count = addedFilters.value.length;
        if (statusIsBlocked.value !== null) count++;
        if (statusIsSmsBlocked.value !== null) count++;
        if (statusHasBounced.value !== null) count++;
        if (statusHasSmsBounced.value !== null) count++;
        if (!props.scalarOnly || props.showTags) {
            if (tags.state.selectedIds.length > 0) count++;
            if (missingTags.value.length > 0) count++;
        }
        if (!props.scalarOnly) {
            if (activeCategories.value.length > 0) count++;
            if (blockedCategories.value.length > 0) count++;
        }
        return count;
    });

    // --- Build OData filter string ---

    function escapeOdata(text: string): string {
        // % must be escaped first so it doesn't double-encode the %XX sequences introduced below.
        return text
            .replace(/%/g, '%25')
            .replace(/'/g, "''")
            .replace(/#/g, '%23')
            .replace(/\+/g, '%2B')
            .replace(/&/g, '%26');
    }

    function buildOdataFilter(): string {
        let filter = '';

        if (!props.scalarOnly || props.showTags) {
            // Tags
            for (const tagId of tags.state.selectedIds) {
                if (filter) filter += ' and ';
                filter += `Tags/any(tag:tag/TagId eq ${guidLiteral(tagId)})`;
            }
            for (const tagId of missingTags.value) {
                if (filter) filter += ' and ';
                filter += `Tags/all(tag:tag/TagId ne ${guidLiteral(tagId)})`;
            }
        }

        if (!props.scalarOnly) {
            // Categories
            for (const catId of activeCategories.value) {
                if (filter) filter += ' and ';
                filter += `Categories/all(category:category/CategoryId ne ${guidLiteral(catId)})`;
            }
            for (const catId of blockedCategories.value) {
                if (filter) filter += ' and ';
                filter += `Categories/any(category:category/CategoryId eq ${guidLiteral(catId)})`;
            }
        }

        // Added field filters
        const conditions: string[] = [];
        for (const f of addedFilters.value) {
            const name = f.fieldName;
            let text = escapeOdata(f.text);
            let cond = '';

            if (f.operator === 'empty') {
                cond = f.fieldType === 'string' ? `(${name} eq '' or ${name} eq null)` : `(${name} eq null)`;
            } else if (f.operator === 'notEmpty') {
                cond = f.fieldType === 'string' ? `(${name} ne '' and ${name} ne null)` : `(${name} ne null)`;
            } else {
                let val: string | null;
                if (f.fieldType === 'datetime') {
                    // datetime-local input yields 'yyyy-MM-ddTHH:mm' (no seconds/offset) — OData v4's
                    // Edm.DateTimeOffset literal requires both, or the backend rejects the query outright.
                    // The backend is pinned to ODataOptions.TimeZone = Utc, which makes a 'Z'-labeled
                    // literal an identity conversion when it's bound to a DateTime-typed property — so
                    // sending the exact typed digits (no client-side conversion) reproduces the old v3
                    // pipeline's behavior of comparing raw, unconverted values.
                    if (!text) {
                        val = null;
                    } else if (!props.odataV4) {
                        val = `datetime'${text}'`;
                    } else {
                        const hasSeconds = /T\d{2}:\d{2}:\d{2}/.test(text);
                        val = hasSeconds ? `${text}Z` : `${text}:00Z`;
                    }
                }
                // Swedish input uses ',' as the decimal separator; OData numeric literals require '.'
                // (same normalization PropertySegmentExpression.cs already applies server-side).
                else if (f.fieldType === 'number') val = text ? text.replace(/,/g, '.') : null;
                else if (f.fieldType === 'boolean') val = text === 'true' || text === 'false' ? text : null;
                else val = text ? `'${text}'` : null;

                if (f.operator === 'contains') {
                    cond = val ? `(${name} ne null and ${name} ne '' and indexof(${name}, ${val}) ge 0)` : `(${name} eq null)`;
                } else if (f.operator === 'notContains') {
                    cond = val ? `(${name} ne null and ${name} ne '' and indexof(${name}, ${val}) lt 0)` : `(${name} ne null)`;
                } else if (f.operator === 'eq') {
                    cond = `(${name} eq ${val})`;
                } else if (f.operator === 'ne') {
                    cond = `(${name} ne ${val})`;
                } else if (f.operator === 'gt') {
                    if (val) cond = `(${name} gt ${val})`;
                } else if (f.operator === 'lt') {
                    if (val) cond = `(${name} lt ${val})`;
                } else if (f.operator === 'startsWith') {
                    cond = val ? `(${name} ne null and startswith(${name}, ${val}) eq true)` : `(${name} eq null)`;
                } else if (f.operator === 'notStartsWith') {
                    cond = val ? `(${name} ne null and startswith(${name}, ${val}) eq false)` : `(${name} ne null)`;
                } else if (f.operator === 'endsWith') {
                    cond = val ? `(${name} ne null and endswith(${name}, ${val}) eq true)` : `(${name} eq null)`;
                }
            }
            if (cond) conditions.push(cond);
        }

        if (conditions.length > 0) {
            const group = '(' + conditions.join(` ${andOrOperator.value} `) + ')';
            if (filter) filter += ' and ';
            filter += group;
        }

        // Status toggles
        if (statusIsBlocked.value !== null) {
            if (filter) filter += ' and ';
            filter += `IsActive eq ${!statusIsBlocked.value}`;
        }
        if (statusIsSmsBlocked.value !== null) {
            if (filter) filter += ' and ';
            filter += `IsSmsActive eq ${!statusIsSmsBlocked.value}`;
        }
        if (statusHasBounced.value !== null) {
            if (filter) filter += ' and ';
            filter += `HasBounced eq ${statusHasBounced.value}`;
        }
        if (statusHasSmsBounced.value !== null) {
            if (filter) filter += ' and ';
            filter += `HasSmsBounced eq ${statusHasSmsBounced.value}`;
        }

        return filter.replace(/\s/g, '+');
    }

    // Emit on any change
    watch(
        [
            addedFilters,
            andOrOperator,
            statusIsBlocked,
            statusIsSmsBlocked,
            statusHasBounced,
            statusHasSmsBounced,
            () => tags.state.selectedIds,
            missingTags,
            activeCategories,
            blockedCategories,
        ],
        () => {
            emit('change', buildOdataFilter());
            emit('update:filterState', serializeFilterState());
        },
        { deep: true }
    );

    defineExpose({
        filterCount,
        addedFilters,
        andOrOperator,
        statusIsBlocked,
        statusIsSmsBlocked,
        statusHasBounced,
        statusHasSmsBounced,
        missingTags,
        activeCategories,
        blockedCategories,
        categories,
        getFilterValueDisplay,
        clearFilter: removeFilter,
        clearStatusIsBlocked,
        clearStatusIsSmsBlocked,
        clearStatusHasBounced,
        clearStatusHasSmsBounced,
        clearMissingTags,
        clearActiveCategories,
        clearBlockedCategories,
        clearAllFilters,
        setPresetFilters,
        serializeFilterState,
        restoreFilterState,
    });
</script>

<template>
    <div class="filter-wrapper">
        <h4>{{ $Resources.FilterHeading }}</h4>

        <!-- Field / Operator / Value builder -->
        <div class="row">
            <div class="col-sm-4">
                <div class="form-group m-sm-b">
                    <FormSelect :label="$Resources.FilterContactFieldsLabel" v-model="selectedFieldName" :options="fieldOptions" :emptyOption="true" />
                </div>
            </div>
            <div class="col-sm-4">
                <div class="form-group m-sm-b">
                    <FormSelect
                        :label="$Resources.FilterOperatorsLabel"
                        v-model="selectedOperatorKey"
                        :options="operatorOptions"
                        :emptyOption="true"
                        :disabled="!selectedField" />
                </div>
            </div>
            <div class="col-sm-4">
                <div class="form-group m-sm-b">
                    <FormSelect
                        v-if="selectedField?.type === 'boolean'"
                        :label="$Resources.FilterTextLabel"
                        v-model="filterText"
                        :options="booleanOptions"
                        :emptyOption="true"
                        :disabled="!enableFilterText" />
                    <FormInput
                        v-else
                        :label="selectedField?.type === 'datetime' ? $Resources.FilterDateTimeLabel : $Resources.FilterTextLabel"
                        v-model="filterText"
                        :type="selectedField?.type === 'datetime' ? 'datetime-local' : 'text'"
                        :placeholder="$Resources.FilterTextPlaceholder"
                        :disabled="!enableFilterText" />
                </div>
            </div>
        </div>

        <div class="form-group m-sm-b d-flex align-items-center gap-sm">
            <button class="btn btn-primary btn-sm" :disabled="!canAddFilter" @click="addFilter">
                {{ $Resources.FilterAddFilterBtnText }}
            </button>
            <span class="small fw-600 m-auto-l">{{ $Resources.FilterCombineLabel }}:</span>
            <div class="btn-group">
                <label class="btn btn-default btn-sm" :class="{ active: andOrOperator === 'and' }">
                    <input class="hide-off-screen" type="radio" value="and" v-model="andOrOperator" />
                    {{ $Resources.OperatorAndLabel }}
                </label>
                <label class="btn btn-default btn-sm" :class="{ active: andOrOperator === 'or' }">
                    <input class="hide-off-screen" type="radio" value="or" v-model="andOrOperator" />
                    {{ $Resources.OperatorOrLabel }}
                </label>
            </div>
        </div>

        <h5 class="m-md-t">{{ $Resources.FilterStatusHeading }}</h5>
        <div class="row">
            <div class="col-sm-3">
                <FormRadioGroup
                    :clearable="true"
                    :legend="$Resources.ContactFilterIsBlockedLabel + ':'"
                    legendClasses="label-look m-xs-b"
                    :options="[
                        { label: $Resources.YesLabel, value: true },
                        { label: $Resources.NoLabel, value: false },
                    ]"
                    :switchStyle="SwitchStyle.Button"
                    :class="{ 'btn-danger-is-selected': statusIsBlocked, 'btn-success-is-selected': !statusIsBlocked }"
                    v-model="statusIsBlocked"
                    class="btn-group btn-group-sm m-sm-b d-inline-flex flex-even" />
            </div>
            <div class="col-sm-3">
                <FormRadioGroup
                    :clearable="true"
                    :legend="$Resources.ContactFilterIsSmsBlockedLabel + ':'"
                    legendClasses="label-look m-xs-b"
                    :options="[
                        { label: $Resources.YesLabel, value: true },
                        { label: $Resources.NoLabel, value: false },
                    ]"
                    :switchStyle="SwitchStyle.Button"
                    :class="{ 'btn-danger-is-selected': statusIsSmsBlocked, 'btn-success-is-selected': !statusIsSmsBlocked }"
                    v-model="statusIsSmsBlocked"
                    class="btn-group btn-group-sm m-sm-b d-inline-flex flex-even" />
            </div>
            <div class="col-sm-3">
                <FormRadioGroup
                    :clearable="true"
                    :legend="$Resources.ContactFilterHasBouncedLabel + ':'"
                    legendClasses="label-look m-xs-b"
                    :options="[
                        { label: $Resources.YesLabel, value: true },
                        { label: $Resources.NoLabel, value: false },
                    ]"
                    :switchStyle="SwitchStyle.Button"
                    :class="{ 'btn-danger-is-selected': statusHasBounced, 'btn-success-is-selected': !statusHasBounced }"
                    v-model="statusHasBounced"
                    class="btn-group btn-group-sm m-sm-b d-inline-flex flex-even" />
            </div>
            <div class="col-sm-3">
                <FormRadioGroup
                    :clearable="true"
                    :legend="$Resources.ContactFilterHasSmsBouncedLabel + ':'"
                    legendClasses="label-look m-xs-b"
                    :options="[
                        { label: $Resources.YesLabel, value: true },
                        { label: $Resources.NoLabel, value: false },
                    ]"
                    :switchStyle="SwitchStyle.Button"
                    :class="{ 'btn-danger-is-selected': statusHasSmsBounced, 'btn-success-is-selected': !statusHasSmsBounced }"
                    v-model="statusHasSmsBounced"
                    class="btn-group btn-group-sm m-sm-b d-inline-flex flex-even" />
            </div>
        </div>

        <template v-if="!scalarOnly || showTags">
            <!-- Tags -->
            <h5>{{ $Resources.FilterTagsHeading }}</h5>
            <div class="row">
                <div class="col-sm-6">
                    <MultiSelect
                        class="form-group m-sm-b"
                        :label="$Resources.FilterTagsLabel"
                        :items="tags.state.list"
                        selectProp="TagId"
                        displayProp="Title"
                        selectOnly="TagId"
                        :selectedItems="tags.state.selectedIds"
                        @update:selectedItems="tags.state.selectedIds = $event" />
                </div>
                <div class="col-sm-6">
                    <MultiSelect
                        class="form-group m-sm-b"
                        :label="$Resources.FilterMissingTagsLabel"
                        :items="tags.state.list"
                        selectProp="TagId"
                        displayProp="Title"
                        selectOnly="TagId"
                        :selectedItems="missingTags"
                        @update:selectedItems="missingTags = $event" />
                </div>
            </div>
        </template>

        <template v-if="!scalarOnly">
            <!-- Subscriptions -->
            <h5 class="m-xs-b">{{ $Resources.FilterSubscriptionsHeading }}</h5>
            <div class="row">
                <div class="col-sm-6">
                    <MultiSelect
                        class="form-group m-sm-b"
                        :label="$Resources.FilterCategoriesLabel"
                        :items="categories"
                        selectProp="CategoryId"
                        displayProp="Name"
                        selectOnly="CategoryId"
                        :selectedItems="activeCategories"
                        @update:selectedItems="activeCategories = $event" />
                </div>
                <div class="col-sm-6">
                    <MultiSelect
                        class="form-group m-sm-b"
                        :label="$Resources.FilterBlockedCategoriesLabel"
                        :items="categories"
                        selectProp="CategoryId"
                        displayProp="Name"
                        selectOnly="CategoryId"
                        :selectedItems="blockedCategories"
                        @update:selectedItems="blockedCategories = $event" />
                </div>
            </div>
        </template>
    </div>
</template>

<style lang="less">
    .filter-wrapper {
        min-width: 600px;
    }

    .dropdown-menu-content:has(.filter-wrapper) {
        padding-bottom: var(--ug-spacing-xs);
    }
</style>
